import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, Page, TextAreaField, Banner } from "./components.js";
import { useSession } from "./session.js";
import { titleCase } from "./format.js";
const THEME_KEY = "gweb.theme";
const readTheme = () => { try {
    const v = localStorage.getItem(THEME_KEY);
    return v === "light" || v === "dark" ? v : "system";
}
catch {
    return "system";
} };
function applyTheme(t) {
    if (t === "system")
        document.documentElement.removeAttribute("data-theme");
    else
        document.documentElement.setAttribute("data-theme", t);
}
export function useTheme() {
    const [theme, setTheme] = useState(readTheme);
    useEffect(() => { applyTheme(theme); }, [theme]);
    const cycle = useCallback(() => {
        setTheme((t) => {
            const next = t === "system" ? "light" : t === "light" ? "dark" : "system";
            try {
                if (next === "system")
                    localStorage.removeItem(THEME_KEY);
                else
                    localStorage.setItem(THEME_KEY, next);
            }
            catch { /* ignore */ }
            return next;
        });
    }, []);
    return { theme, cycle };
}
export function AppShell({ brand, nav, children, footer }) {
    const { claims, signOut } = useSession();
    const { theme, cycle } = useTheme();
    return (_jsxs("div", { className: "g-shell", children: [_jsx("a", { href: "#main", className: "g-skip", children: "Skip to content" }), _jsx("header", { className: "g-header", children: _jsxs("div", { className: "g-header-inner", children: [_jsx("div", { className: "g-brand", children: brand }), nav && _jsx("nav", { className: "g-nav", "aria-label": "Main", children: nav }), _jsxs("div", { className: "g-user", children: [claims && (_jsxs("div", { className: "g-user-chip", title: claims.roles.join(", "), children: [_jsx("span", { className: "g-user-name", children: claims.sub }), _jsx("span", { className: "g-user-roles", children: claims.roles.map((r) => _jsx(Badge, { tone: "accent", children: titleCase(r.replace(/^gweb_|^customer_|^svc_/, "")) }, r)) })] })), _jsx(Button, { variant: "ghost", onClick: cycle, "aria-label": `Theme: ${theme}. Click to change.`, children: theme === "dark" ? "Dark" : theme === "light" ? "Light" : "Auto" }), _jsx(Button, { variant: "ghost", onClick: signOut, children: "Sign out" })] })] }) }), _jsx("main", { id: "main", className: "g-main", tabIndex: -1, children: children }), _jsx("footer", { className: "g-footer", children: footer ?? "GWEB" })] }));
}
/** Development sign-in. A real identity provider replaces this page with a redirect; nothing else changes. */
export function SignInPage({ title, mintCommand }) {
    const { signIn } = useSession();
    const [raw, setRaw] = useState("");
    const [error, setError] = useState(null);
    useTheme();
    return (_jsx("div", { className: "g-signin", children: _jsx(Card, { title: title, children: _jsxs("form", { className: "g-stack", onSubmit: (e) => {
                    e.preventDefault();
                    try {
                        signIn(raw);
                        setRaw("");
                    }
                    catch (err) {
                        setError(err instanceof Error ? err.message : "Could not sign in.");
                    }
                }, children: [_jsx(Banner, { tone: "warn", title: "Development sign-in", children: "Real sign-in is not set up yet. Paste a token minted for your role. It is kept for this browser tab only and is removed when you sign out or it expires." }), _jsx(TextAreaField, { label: "Token", rows: 4, value: raw, onChange: (e) => { setRaw(e.target.value); setError(null); }, error: error ?? undefined, spellCheck: false, autoComplete: "off", required: true, hint: "Starts with eyJ\u2026" }), _jsxs("details", { className: "g-details", children: [_jsx("summary", { children: "How do I get a token?" }), _jsxs("p", { children: ["From the ", _jsx("code", { children: "gweb-platform" }), " folder, with the same secret the services use:"] }), _jsx("pre", { className: "g-pre", children: mintCommand })] }), _jsx(Button, { type: "submit", variant: "primary", disabled: raw.trim() === "", children: "Sign in" })] }) }) }));
}
export function NotAllowed({ what }) {
    return _jsx(Page, { title: "Not available", children: _jsx(Banner, { tone: "warn", title: "Your role cannot use this page", children: what }) });
}
